/* eslint-disable no-nested-ternary */

import { LOCATION_CHANGE } from 'connected-react-router';
import { combineEpics, ofType } from 'redux-observable';
import { defer, EMPTY, of } from 'rxjs';
import {
  catchError,
  filter,
  ignoreElements,
  map,
  mergeMap,
  pairwise,
  startWith,
  takeUntil,
  skip,
  switchMap,
} from 'rxjs/operators';

import { subscribeOpenPositionSpot, unsubscribeOpenPositionSpot } from 'actions/socket';
import { isAuthenticated } from 'actions/user';
import {
  SOCKET_CONNECTED,
  SUBSCRIBE_OPEN_POSITION_SPOT,
  SUBSCRIBE_PRICE_ALERT_COIN_SPOT,
  SUBSCRIBE_SELECTED_PRODUCT_SPOT,
  SUBSCRIBE_SPOT,
  SUBSCRIBE_WALLET_SPOT,
  UNSUBSCRIBE_ALL,
  UNSUBSCRIBE_OPEN_POSITION_SPOT,
  UNSUBSCRIBE_PRICE_ALERT_COIN_SPOT,
  UNSUBSCRIBE_SELECTED_PRODUCT_SPOT,
  UNSUBSCRIBE_SPOT,
  UNSUBSCRIBE_WALLET_SPOT,
} from 'actionTypes/socket';
import { isOnSpotRouteNative } from 'components/SpotTradingIndia/Modal/helpers';
import {
  HOME_PAGE_ASSETS_FOR_SPOT_SYMBOLS,
  IS_INDIAN_EXCHANGE,
} from 'constants/constants';
import { isNotEmpty } from 'ramdax';
import { socketActiveSelector } from 'selectors/socketSelectors';
import { isSpotModalOpenSelector } from 'selectors/spotSelector';
import { selectedProductSelector } from 'selectors/tradeSelectors';
import {
  spotIndiaSymbolsSelector,
  spotIndiaSymbolsSelectorWithBalance,
  spotIndiaTickerSelector,
} from 'selectors/walletSelector';
import { useSpotIndiaStoreBase } from 'stores/spotIndia/spotIndia.store';
import { updateOpenPositionSpotPrices } from 'stores/store.globalActions/24hrTicker';
import { updateWalletSpotPrices } from 'stores/store.globalActions/priceIndex';
import {
  updateSelectedProductSpot,
  updateSelectedCoinSpotPrice,
} from 'stores/store.globalActions/selectedProduct';

import {
  getPositionAnalyticsAssetFromSearch,
  getSpotIndexSymbolFromAsset,
  getSpotSymbolFromProductSymbol,
  isPositionAnalyticsRoute,
} from './helper';
import { enabledWalletSpotSymbols, wsSubject } from './socket';

// India-specific action types
export const SPOT_INDIA_SUBSCRIBE = 'spot_india_subscribe';
export const SPOT_INDIA_UNSUBSCRIBE = 'spot_india_unsubscribe';
export const SPOT_INDIA_TICKER_SUBSCRIBE = 'spot_india_ticker_subscribe';
export const SPOT_INDIA_TICKER_UNSUBSCRIBE = 'spot_india_ticker_unsubscribe';

// India-specific action creators
export const subscribeSpotIndia = symbols => ({
  type: SPOT_INDIA_SUBSCRIBE,
  payload: symbols,
});
export const unsubscribeSpotIndia = () => ({ type: SPOT_INDIA_UNSUBSCRIBE });
export const subscribeSpotIndiaTicker = () => ({ type: SPOT_INDIA_TICKER_SUBSCRIBE });
export const unsubscribeSpotIndiaTicker = () => ({ type: SPOT_INDIA_TICKER_UNSUBSCRIBE });

const payloadGenerator = (type, name, symbols) => {
  return {
    type,
    payload: {
      channels: [
        {
          name,
          symbols,
        },
      ],
    },
  };
};

const spotChannel = symbols =>
  wsSubject.multiplex(
    () => payloadGenerator('subscribe', 'v2/spot_price', symbols),
    () => payloadGenerator('unsubscribe', 'v2/spot_price', symbols),
    message => message.type === 'v2/spot_price'
  );

const subscribeSpotChannel = symbol =>
  wsSubject.multiplex(
    () => payloadGenerator('subscribe', 'v2/spot_price', symbol),
    () => {},
    message => message.type === 'v2/spot_price'
  );

const unsubscribeSpotChannel = symbol =>
  wsSubject.multiplex(
    () => payloadGenerator('unsubscribe', 'v2/spot_price', symbol),
    () => {},
    message => message.type === 'v2/spot_price'
  );

// India-specific channel helper for ticker
const tickerIndiaChannel = symbols =>
  wsSubject.multiplex(
    () => payloadGenerator('subscribe', 'v2/ticker', symbols),
    () => payloadGenerator('unsubscribe', 'v2/ticker', symbols),
    message => message.type === 'v2/ticker'
  );

export const unsubscribeSelectedProductSpotChannelEpic = (action$, state$) =>
  action$.pipe(
    ofType(UNSUBSCRIBE_SELECTED_PRODUCT_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    mergeMap(action =>
      unsubscribeSpotChannel([action.payload]).pipe(
        catchError(err => {
          console.error('UNSUBSCRIBE_SELECTED_PRODUCT_SPOT', err);
          return EMPTY;
        })
      )
    )
  );

export const subscribeSelectedProductSpotChannelEpic = (action$, state$) =>
  action$.pipe(
    ofType(SUBSCRIBE_SELECTED_PRODUCT_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    mergeMap(action =>
      subscribeSpotChannel([action.payload]).pipe(
        takeUntil(action$.ofType(UNSUBSCRIBE_ALL)),
        catchError(err => {
          console.error('Selected product spot', err);
          return EMPTY;
        })
      )
    ),
    filter(data => {
      const symbol = selectedProductSelector(state$.value)?.spot_index?.symbol;
      return isNotEmpty(data) && symbol === data.s;
    }),
    // map(data => updateSpotMarkData({ symbol: data.s, price: data.p })), // updateChartSpotData
    map(data => updateSelectedProductSpot({ symbol: data.s, price: data.p })),
    ignoreElements()
  );

export const subscribeWalletSpotChannelEpic = (action$, state$) =>
  action$.pipe(
    ofType(SUBSCRIBE_WALLET_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    // is not a indian exchange and user is authenticated
    filter(() => !IS_INDIAN_EXCHANGE && isAuthenticated(state$.value.user)),
    mergeMap(() => {
      const spotSymbols = enabledWalletSpotSymbols();
      return spotChannel(spotSymbols).pipe(
        takeUntil(action$.ofType(UNSUBSCRIBE_ALL, UNSUBSCRIBE_WALLET_SPOT)),
        catchError(err => {
          console.error('Wallet spot channels', err);
          return EMPTY;
        })
      );
    }),
    filter(isNotEmpty),
    map(updateWalletSpotPrices)
  );

export const subscribeOpenPositionsSpotEpic = (action$, state$) =>
  action$.pipe(
    ofType(SUBSCRIBE_OPEN_POSITION_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      const socketActive = socketActiveSelector(state$.value);
      return !isMinimalUpdateUser && socketActive;
    }),
    mergeMap(action => {
      return subscribeSpotChannel([action.payload]).pipe(
        catchError(err => {
          console.error('Wallet spot channels', err);
          return EMPTY;
        })
      );
    }),
    filter(isNotEmpty),
    map(updateOpenPositionSpotPrices)
  );

export const unsubscribeOpenPositionsSpotEpic = (action$, state$) =>
  action$.pipe(
    ofType(UNSUBSCRIBE_OPEN_POSITION_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    filter(action => {
      const selectedProduct = selectedProductSelector(state$.value);
      if (!selectedProduct) return false;
      const symbol = selectedProduct?.spot_index?.symbol;
      if (enabledWalletSpotSymbols().includes(action.payload)) return false;
      if (symbol === action.payload) return false;
      return true;
    }),
    mergeMap(action => {
      return unsubscribeSpotChannel(action.payload).pipe(
        catchError(err => {
          console.error('Wallet spot channels', err);
          return EMPTY;
        })
      );
    })
  );

export const subscribeSpotForHomePageEpic = (action$, state$) =>
  action$.pipe(
    ofType(SUBSCRIBE_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    mergeMap(() => {
      const symbols = HOME_PAGE_ASSETS_FOR_SPOT_SYMBOLS;
      return subscribeSpotChannel(symbols).pipe(
        catchError(err => {
          console.error('Wallet spot channels', err);
          return EMPTY;
        })
      );
    }),
    filter(isNotEmpty),
    map(updateWalletSpotPrices)
  );

export const unSubscribeSpotForHomePageEpic = (action$, state$) =>
  action$.pipe(
    ofType(UNSUBSCRIBE_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    mergeMap(action => {
      const symbol = action.payload;
      const spotSymbol = getSpotSymbolFromProductSymbol(symbol);
      const symbolToUnsubscribe = HOME_PAGE_ASSETS_FOR_SPOT_SYMBOLS.filter(
        s => s !== spotSymbol && !enabledWalletSpotSymbols().includes(s)
      );
      if (symbolToUnsubscribe.length === 0) {
        return EMPTY;
      }
      return unsubscribeSpotChannel(symbolToUnsubscribe).pipe(
        catchError(err => {
          console.error('Wallet spot channels', err);
          return EMPTY;
        })
      );
    })
  );

export const subscribeSpotForSelectedCoinEpic = (action$, state$) =>
  action$.pipe(
    ofType(SUBSCRIBE_PRICE_ALERT_COIN_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    mergeMap(action => {
      const { symbol } = action.payload;
      return subscribeSpotChannel([symbol]).pipe(
        catchError(err => {
          console.error('Selected coin spot channels', err);
          return EMPTY;
        })
      );
    }),
    filter(isNotEmpty),
    map(updateSelectedCoinSpotPrice)
  );

export const unSubscribeSpotForSelectedCoinEpic = (action$, state$) =>
  action$.pipe(
    ofType(UNSUBSCRIBE_PRICE_ALERT_COIN_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    mergeMap(action => {
      const { symbol } = action.payload;
      return unsubscribeSpotChannel([symbol]).pipe(
        catchError(err => {
          console.error('Selected coin spot channels', err);
          return EMPTY;
        })
      );
    })
  );

export const positionAnalyticsRouteSpotSubscriptionEpic = action$ =>
  action$.pipe(
    ofType(LOCATION_CHANGE),
    filter(({ payload }) => isPositionAnalyticsRoute(payload.location.pathname)),
    map(({ payload }) => getPositionAnalyticsAssetFromSearch(payload.location.search)),
    startWith(null),
    pairwise(),
    filter(([prevAsset, currAsset]) => prevAsset !== currAsset),
    mergeMap(([prevAsset, currAsset]) => {
      const actions = [];
      if (prevAsset) {
        const prevSpotSymbol = getSpotIndexSymbolFromAsset(prevAsset);
        if (prevSpotSymbol) {
          actions.push(unsubscribeOpenPositionSpot(prevSpotSymbol));
        }
      }

      if (currAsset) {
        const currSpotSymbol = getSpotIndexSymbolFromAsset(currAsset);
        if (currSpotSymbol) {
          actions.push(subscribeOpenPositionSpot(currSpotSymbol));
        }
      }

      return of(...actions);
    })
  );

// India-specific reconnection epic - only triggers on reconnection, not initial connection
export const spotIndiaReconnectionEpic = (action$, state$) =>
  action$.pipe(
    ofType(SOCKET_CONNECTED),
    skip(1),
    filter(() => socketActiveSelector(state$.value)),
    map(() => {
      const isSpotModalOpen = isSpotModalOpenSelector(state$.value);

      return isSpotModalOpen
        ? subscribeSpotIndia()
        : subscribeSpotIndia(spotIndiaSymbolsSelectorWithBalance(state$.value));
    })
  );

// India-specific epics
export const spotIndiaSpotPriceEpic = (action$, state$) =>
  action$.pipe(
    ofType(SPOT_INDIA_SUBSCRIBE),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    switchMap(action =>
      defer(() => {
        let symbolArray;
        if (action.payload) {
          symbolArray = Object.values(action.payload);
        } else {
          const symbols = spotIndiaSymbolsSelector(state$.value);
          symbolArray = symbols ? Object.values(symbols) : [];
        }

        return spotChannel(symbolArray);
      }).pipe(
        takeUntil(action$.ofType(UNSUBSCRIBE_ALL, SPOT_INDIA_UNSUBSCRIBE)),
        catchError(err => {
          console.error('India spot price channel error', err);
          return EMPTY;
        })
      )
    ),
    filter(data => isNotEmpty(data) && data.s && data.p),
    map(data => {
      useSpotIndiaStoreBase.getState()?.actions?.updateSpotPrices({ [data.s]: data.p });
      return data;
    }),
    ignoreElements()
  );

export const spotIndiaTickerEpic = (action$, state$) =>
  action$.pipe(
    ofType(SPOT_INDIA_TICKER_SUBSCRIBE, SOCKET_CONNECTED),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      const socketActive = socketActiveSelector(state$.value);
      const isSpotModalOpen = isSpotModalOpenSelector(state$.value);
      const isOnSpotRoute = isOnSpotRouteNative();
      return !isMinimalUpdateUser && socketActive && (isSpotModalOpen || isOnSpotRoute);
    }),
    switchMap(() =>
      defer(() => {
        const symbols = spotIndiaTickerSelector(state$.value);
        const symbolArray = symbols ? Object.values(symbols) : [];

        return tickerIndiaChannel(symbolArray);
      }).pipe(
        takeUntil(action$.ofType(UNSUBSCRIBE_ALL, SPOT_INDIA_TICKER_UNSUBSCRIBE)),
        catchError(err => {
          console.error('India ticker channel error', err);
          return EMPTY;
        })
      )
    ),
    filter(data => isNotEmpty(data) && data.symbol && data.open && data.close),
    map(data => {
      useSpotIndiaStoreBase.getState()?.actions?.updateSpotTickerData({
        [data.symbol]: {
          open: data.open,
          close: data.close,
          high: data.high,
          low: data.low,
          ltp_change_24h: data.ltp_change_24h,
          mark_change_24h: data.mark_change_24h,
        },
      });
      return data;
    }),
    ignoreElements()
  );

export default combineEpics(
  subscribeSelectedProductSpotChannelEpic,
  unsubscribeSelectedProductSpotChannelEpic,
  subscribeWalletSpotChannelEpic,
  subscribeOpenPositionsSpotEpic,
  unsubscribeOpenPositionsSpotEpic,
  subscribeSpotForHomePageEpic,
  unSubscribeSpotForHomePageEpic,
  subscribeSpotForSelectedCoinEpic,
  unSubscribeSpotForSelectedCoinEpic,
  positionAnalyticsRouteSpotSubscriptionEpic,
  spotIndiaReconnectionEpic,
  spotIndiaSpotPriceEpic,
  spotIndiaTickerEpic
);
